import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { MatFormField, MatOption, MatSelect, MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { ReplaySubject, Subject } from 'rxjs';
import { MatSelectSearchComponent, NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { AsyncPipe } from '@angular/common';
import { FileTypeFilterStore } from '../../../store/file-type-filter.store';

@Component({
    selector: 'app-file-type-select',
    imports: [
        MatFormField,
        MatSelectModule,
        ReactiveFormsModule,
        MatSelectTrigger,
        MatOption,
        NgxMatSelectSearchModule,
        AsyncPipe
    ],
    templateUrl: './file-type-select.component.html',
    styleUrl: './file-type-select.component.scss'
})
export class FileTypeSelectComponent implements AfterViewInit, OnInit {
  @Input() form: FormGroup;
  @Input() required: boolean = false;
  @Input() controlName: string = 'file-type';
  @Input() appearance = 'outline';
  @Input() floatLabel = 'always';
  @Input() color = 'primary';
  @Input() placeholder = 'Make a Selection';
  @Input() multiple = true;
  @Output() readyEvent = new EventEmitter<void>();
  @Output() valueChangedEvent = new EventEmitter<number[] | null>();
  @ViewChild(MatSelect) matSelect: MatSelect;
  @ViewChild(MatSelectSearchComponent) searchFilter: MatSelectSearchComponent;
  disabled = false;
  control = new FormControl<number[] | null>([]);
  filterControl = new FormControl<string>('');
  valueChanged = signal<any>(null);
  ready = signal<boolean>(false);
  filteredOptions: ReplaySubject<FileTypeSelectOption[]> = new ReplaySubject<FileTypeSelectOption[]>(1);
  allToggled = false;
  indeterminateToggle = false;
  protected _onDestroy = new Subject<void>();
  filterStore = inject(FileTypeFilterStore);
  initialLoad = true;

  // TODO:
  // 'selectOptions()' will display its values according to the org config settings.
  // those settings do not currently control an org's available file types,
  // for now, filter will populate with all file types (MOR, MAO).
  selectOptions = computed<FileTypeSelectOption[]>(() => this.filterStore.fileTypeOptions());

  constructor() {
    effect(() => {
      this.filteredOptions.next(this.selectOptions());
    });
  }

  ngOnInit() {
    if(this.required) {
      this.control.setValidators([Validators.required]);
    }

    if(this.form) {
      this.form.addControl(this.controlName, this.control);
    }

    this.filterControl.valueChanges
    .pipe(takeUntil(this._onDestroy))
    .subscribe(() => {
      this.filterMulti();

      if(this.multiple) {
        this.setAllToggleState();
      }
    });

    if(this.multiple) {
      this.control.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.setAllToggleState();
      });
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.setSelectedValues([]);
    }, 200);
      
    this.initialLoad = false;
    this.ready.set(true);
    this.readyEvent.emit();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  onChange() {
    if (this.control.value) {
      this.valueChanged.set(this.control.value);
      this.valueChangedEvent.emit(this.control.value);
    } else {
      this.valueChanged.set(null);
      this.valueChangedEvent.emit(null);
    }
  }

  filterMulti() {
    if (!this.selectOptions()) {
      return;
    }

    let search = this.filterControl?.value;

    if (!search) {
      this.filteredOptions.next(this.selectOptions().slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    this.filteredOptions.next(
      this.selectOptions()
        .filter(o => (o.label.toLowerCase())
        .indexOf(search.toLowerCase()) > -1)
    );
  }

  setAllToggleState() {
    let filteredLength = 0;
    if(this.control?.value) {
      this.selectOptions().forEach(o => {
        if (this.control?.value && this.control?.value.indexOf(o.value) > -1) {
          filteredLength++;
        }
      });

      this.indeterminateToggle = filteredLength > 0 && filteredLength < this.selectOptions().length;
      this.allToggled = filteredLength > 0 && filteredLength === this.selectOptions().length;
    }
  }

  allOptionClicked(isChecked: boolean) {
    this.matSelect.options.forEach((o, index) => {
      if(index == 0) {
        return;
      }

      isChecked ? o.select() : o.deselect();
    });

    this.selectOptions().forEach(o => {
      o.selected = isChecked;
    });
  }

  toggleSelection(option: MatOption, item: FileTypeSelectOption) {
    item.selected = option.selected;
  }

  getSelectCount() {
    if (this.control.value)
      return (this.control.value?.length <= this.selectOptions().length) 
        ? this.control.value?.length 
        : this.control.value?.length - 1;
  }

  getLabel(id: number) {
    return this.selectOptions().find(o => o.value === id)?.label;
  }

  async setSelectedValues(values: number[] | null) {
    if(!values) {
      this.control?.reset();
      this.control?.setValue([]);
      this.searchFilter._reset();
      return;
    }

    if(values.length == 0) {
      this.allOptionClicked(true);    
      this.onChange();
      return;
    }

    const filteredOptions = this.matSelect.options.filter((i, index) => { return i && index > 0 });

    values.forEach(o => {
      const found = filteredOptions.find(i => i.value.ID == o);

      if(found) {
        found.select();
      }
    });

    this.onChange();
  }

  getValues() {
    if(this.allToggled && this.multiple) {
      return [{ID: 0}];
    }
    
    return this.control?.value ? this.control?.value.map(i => i) : [];
  }

  checkAllOptions() {
    let result = true;
    this.selectOptions().forEach(o => {
      if(!o.selected) {
        result = false;
      }
    });

    return result;
  }
}

export class FileTypeSelectOption {
  label: string;
  value: number;
  selected: boolean;
}